/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useContext, useState, useEffect} from 'react'
import {useLocation, useHistory, Link} from 'react-router-dom'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Box,
    Button,
    Flex,
    Heading,
    Text,
    Modal,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalOverlay,
    Stack,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentBasket} from './use-current-basket'
import useMultiSite from './use-multi-site'
import RecommendedProducts from '../components/recommended-products'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import {getPriceData, getDisplayVariationValues, getProductType} from '../utils/product-utils'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import DisplayPrice from '../components/display-price'
import {useSplioService} from './use-splio-service'
import {CloseIcon} from '../components/custom-icons'
import PromoCallout from '../components/product-tile/promo-callout'
import DynamicImage from '@app/components/dynamic-image'

// custom hooks
import {useConversionRate} from '../custom-contexts'
import {useAddToCartRecommendations} from './use-add-to-cart-recommendations'

// utils
import {getConvertedCurrencyCode, getConvertedPrice, formatPrice} from '../utils/price-utils'

import useShipToCountry from './use-ship-to-country'
import BasketMgr from '../custom-api/basket-custom-api'

/**
 * This is the context for managing the AddToCartModal.
 * Used in top level App component.
 */
export const AddToCartModalContext = React.createContext()
export const useAddToCartModalContext = () => useContext(AddToCartModalContext)
export const AddToCartModalProvider = ({children}) => {
    const addToCartModal = useAddToCartModal()
    return (
        <AddToCartModalContext.Provider value={addToCartModal}>
            {children}
            <AddToCartModal />
        </AddToCartModalContext.Provider>
    )
}
AddToCartModalProvider.propTypes = {
    children: PropTypes.node.isRequired
}

/**
 * Visual feedback (a modal) for adding item to the cart.
 */
export const AddToCartModal = () => {
    const {isOpen, onClose, data} = useAddToCartModalContext()
    const conversionsRate = useConversionRate()
    const {shipToCountry} = useShipToCountry()
    const {product, itemsAdded = [], selectedQuantity} = data || {}
    const productType = getProductType(product)
    const intl = useIntl()
    const {isError, data: basket = {}} = useCurrentBasket()
    const size = useBreakpointValue({base: 'full', lg: '2xl', xl: '4xl'})
    const [currentProduct, setCurrentProduct] = useState(product)

    const {recommendationResult, isRecommendationLoading} =
        useAddToCartRecommendations(currentProduct)

    useEffect(() => {
        setCurrentProduct(product)
    }, [product])

    if (!isOpen || isError) {
        return null
    }

    const {currency} = basket
    const numberOfItemsAdded = productType.isBundle
        ? selectedQuantity
        : itemsAdded.reduce((acc, {quantity}) => acc + quantity, 0)

    const bundleImage = findImageGroupBy(product.imageGroups, {
        viewType: 'front'
    })?.images?.[0]

    return (
        <Modal size={size} isOpen={isOpen} onClose={onClose} scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <ModalContent
                containerProps={{'data-testid': 'add-to-cart-modal'}}
                p={[16, null, null, 24]}
            >
                <ModalHeader>
                    <Heading as="h1" fontSize="3xl" textTransform={'uppercase'}>
                        {intl.formatMessage(
                            {
                                defaultMessage:
                                    '{quantity} {quantity, plural, one {item} other {items}} added to cart',
                                id: 'add_to_cart_modal.info.added_to_cart'
                            },
                            {quantity: numberOfItemsAdded}
                        )}
                    </Heading>
                </ModalHeader>
                <ModalCloseButton>
                    <CloseIcon boxSize="14px" />
                </ModalCloseButton>
                <ModalBody pt="32px" pb={['166px', null, null, 0]}>
                    <Flex
                        flexDirection={{base: 'column', lg: 'row'}}
                        justifyContent="space-between"
                        paddingBottom={{base: '0', lg: '8'}}
                    >
                        <Box flex="1 0 66%" pr={{lg: '16', xl: '32'}}>
                            {productType.isBundle && (
                                <Flex
                                    key={product.id}
                                    justifyContent="space-between"
                                    paddingBottom={16}
                                    data-testid="product-added"
                                    role="group"
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Product',
                                        id: 'add_to_cart_modal.aria_label.product'
                                    })}
                                >
                                    <Flex gridGap="4">
                                        <Box
                                            width={['85px', null, '147px']}
                                            mr={[16, null, 24]}
                                            flex="none"
                                        >
                                            {bundleImage && (
                                                <DynamicImage
                                                    data-testid="product-tile-image"
                                                    src={`${
                                                        bundleImage?.disBaseLink ||
                                                        bundleImage?.link
                                                    }[?sw={width}&q=60]`}
                                                    widths={[85, 147, 170, 294]}
                                                    imageProps={{
                                                        alt: bundleImage?.alt,
                                                        loading: 'lazy'
                                                    }}
                                                />
                                            )}
                                        </Box>

                                        <Box>
                                            <Text
                                                fontSize="3xl"
                                                fontFamily="heading"
                                                textDecoration="none"
                                                textTransform="uppercase"
                                            >
                                                {product.name}
                                            </Text>

                                            <Text>
                                                {intl.formatMessage({
                                                    defaultMessage: 'Qty',
                                                    id: 'add_to_cart_modal.label.quantity'
                                                })}
                                                : {numberOfItemsAdded}
                                            </Text>

                                            <Flex
                                                flexDirection="column"
                                                justifyContent="space-between"
                                                marginTop={4}
                                                gridGap={4}
                                            >
                                                {itemsAdded.map(({product, variant, quantity}) => {
                                                    const variationAttributeValues =
                                                        getDisplayVariationValues(
                                                            product.variationAttributes,
                                                            variant.variationValues
                                                        )
                                                    return (
                                                        <Box key={variant.productId}>
                                                            <Text
                                                                color="gray.700"
                                                                fontWeight="700"
                                                                fontSize="sm"
                                                            >
                                                                {product.name}{' '}
                                                                {quantity > 1
                                                                    ? `(${quantity})`
                                                                    : ''}
                                                            </Text>
                                                            <Box
                                                                color="gray.600"
                                                                fontSize="sm"
                                                                fontWeight="500"
                                                            >
                                                                {Object.entries(
                                                                    variationAttributeValues
                                                                ).map(([name, value]) => {
                                                                    return (
                                                                        <Text key={value}>
                                                                            {name}: {value}
                                                                        </Text>
                                                                    )
                                                                })}
                                                            </Box>
                                                        </Box>
                                                    )
                                                })}
                                            </Flex>
                                        </Box>
                                    </Flex>

                                    <Box flex="none" alignSelf="flex-end" fontWeight="600">
                                        <Text>
                                            {formatPrice(
                                                getConvertedPrice(
                                                    product.price * numberOfItemsAdded,
                                                    conversionsRate,
                                                    false
                                                ),
                                                getConvertedCurrencyCode(conversionsRate, currency),
                                                conversionsRate,
                                                shipToCountry?.id,
                                                intl
                                            )}
                                        </Text>
                                    </Box>
                                </Flex>
                            )}
                            {!productType.isBundle &&
                                itemsAdded.map(({product, variant, quantity}, index) => {
                                    const image =
                                        findImageGroupBy(product.imageGroups, {
                                            viewType: 'front',
                                            selectedVariationAttributes: variant.variationValues
                                        })?.images?.[0] || product.image
                                    const priceData = getPriceData(product, {quantity})
                                    const variationAttributeValues =
                                        product.variationAttributes && variant.variationValues
                                            ? getDisplayVariationValues(
                                                  product.variationAttributes,
                                                  variant.variationValues
                                              )
                                            : {}

                                    return (
                                        <Flex
                                            key={variant.productId}
                                            justifyContent="space-between"
                                            marginBottom={index < itemsAdded - 1 ? 0 : 4}
                                            paddingBottom={16}
                                            data-testid="product-added"
                                            role="group"
                                            aria-label={intl.formatMessage({
                                                defaultMessage: 'Product',
                                                id: 'add_to_cart_modal.aria_label.product'
                                            })}
                                        >
                                            <Flex gridGap="4">
                                                <Box
                                                    width={['85px', null, '147px']}
                                                    mr={[16, null, 24]}
                                                    flex="none"
                                                >
                                                    {image && (
                                                        <DynamicImage
                                                            data-testid="product-tile-image"
                                                            src={`${
                                                                image?.disBaseLink || image?.link
                                                            }[?sw={width}&q=60]`}
                                                            widths={[85, 147, 170, 294]}
                                                            imageProps={{
                                                                alt: image?.alt,
                                                                loading: 'lazy'
                                                            }}
                                                        />
                                                    )}
                                                </Box>

                                                <Box>
                                                    <Heading
                                                        as="h2"
                                                        fontSize="3xl"
                                                        textTransform={'uppercase'}
                                                    >
                                                        {product.name || product.productName}
                                                    </Heading>
                                                    <Box mt={10}>
                                                        {priceData && currency && (
                                                            <DisplayPrice
                                                                priceData={priceData}
                                                                quantity={quantity}
                                                                currency={currency}
                                                                promoCallout={
                                                                    <PromoCallout
                                                                        priceData={priceData}
                                                                        product={product}
                                                                    />
                                                                }
                                                            />
                                                        )}
                                                    </Box>
                                                    <Box mt="12">
                                                        {Object.entries(
                                                            variationAttributeValues
                                                        ).map(([name, value]) => {
                                                            return (
                                                                <Box key={value} mb="5px">
                                                                    <Text
                                                                        as="span"
                                                                        color="darkGray"
                                                                        variant="bodyBase2"
                                                                    >
                                                                        {name}:&nbsp;
                                                                    </Text>
                                                                    <Text
                                                                        as="span"
                                                                        variant="bodyBase2"
                                                                    >
                                                                        {value}
                                                                    </Text>
                                                                </Box>
                                                            )
                                                        })}
                                                        <Box>
                                                            <Text
                                                                as="span"
                                                                color="darkGray"
                                                                variant="bodyBase2"
                                                            >
                                                                {intl.formatMessage({
                                                                    defaultMessage: 'Qty',
                                                                    id: 'add_to_cart_modal.label.quantity'
                                                                })}
                                                                :&nbsp;
                                                            </Text>
                                                            <Text as="span" variant="bodyBase2">
                                                                {quantity}
                                                            </Text>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Flex>
                                        </Flex>
                                    )
                                })}
                        </Box>
                        <Box
                            display={['none', 'none', 'none', 'block']}
                            flex="1 0 33%"
                            paddingX={{lg: '0', xl: '0'}}
                            paddingY={{base: '4', lg: '0'}}
                        >
                            <CartDetails />
                        </Box>
                    </Flex>
                    <Box paddingY="8" bgColor="gray.50" mt="16" overflow="hidden">
                        {!isRecommendationLoading && recommendationResult?.length && (
                            <RecommendedProducts
                                title={
                                    <FormattedMessage
                                        defaultMessage="You Might Also Like"
                                        id="add_to_cart_modal.recommended_products.title.might_also_like"
                                    />
                                }
                                products={recommendationResult}
                                mx={{base: -10, md: -10}}
                                isScroller={false}
                                isCarousel={true}
                                isAddToCartModal={true}
                                nbSlidesToShow={{base: 1, md: 3, lg: 4}}
                            />
                        )}
                    </Box>
                </ModalBody>
                <ModalFooter
                    position="fixed"
                    bg="white"
                    width="100%"
                    display={['block', 'block', 'block', 'none']}
                    p={16}
                    left={0}
                    bottom={0}
                    zIndex={1}
                >
                    <CartDetails />
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

AddToCartModal.propTypes = {
    product: PropTypes.shape({
        name: PropTypes.string,
        imageGroups: PropTypes.array
    }),
    variant: PropTypes.shape({
        productId: PropTypes.string,
        variationValues: PropTypes.object
    }),
    quantity: PropTypes.number,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    children: PropTypes.any
}

/**
 * Renders the right side of the modal.
 * The Cart Subtotal
 * Button for navigating to the Cart
 * Button for navigating to the Checkout
 * @returns {JSX.Element}
 */
export const CartDetails = () => {
    const conversionsRate = useConversionRate()
    const {shipToCountry} = useShipToCountry()

    const intl = useIntl()
    const {buildUrl, site, locale} = useMultiSite()
    const {app: appConfig} = getConfig()

    const basketMgr = new BasketMgr({
        parameters: {
            siteId: site.id,
            organizationId: appConfig.commerceAPI.parameters.organizationId,
            locale: locale.id
        }
    })
    const {
        data: basket = {},
        derivedData: {totalItems, giftCertificateLineItemCost},
        refetch
    } = useCurrentBasket()
    const {getTokenWhenReady} = useAccessToken()

    const history = useHistory()
    const checkoutURL = buildUrl('/checkout')
    const isInternationalOrder =
        basket?.c_isInternationalOrder || basket?.c_internationalShipping || false

    /**
     * Handles the checkout redirect action by performing necessary operations such as
     * obtaining an access token, assigning the basket, refetching data, and navigating
     * to the checkout URL.
     *
     * @async
     * @param {Event} e - The event object associated with the action, typically a click event.
     * @returns {Promise<void>} A promise that resolves when the redirect action is complete.
     */
    const handleCheckoutRedirectAction = async (e) => {
        e.preventDefault()
        // Get the access token and perform basket assignments
        const accessToken = await getTokenWhenReady()
        await basketMgr.sfsBasketAssignments(accessToken, site.id, locale.id)
        await refetch()

        history.push(checkoutURL)
    }

    return (
        <>
            <Flex justifyContent="space-between" alignItems={'baseline'} marginBottom="16">
                <Text variant="bodyLarge2" color="darkGray">
                    <FormattedMessage
                        defaultMessage="Cart Subtotal{br}({itemAccumulatedCount} {itemAccumulatedCount, plural, one {item} other {items}})"
                        id="add_to_cart_modal.label.cart_subtotal"
                        values={{
                            br: <br />,
                            itemAccumulatedCount: totalItems
                        }}
                    />
                </Text>
                <Text variant="bodyLarge1">
                    {formatPrice(
                        isInternationalOrder && basket?.c_internationalProductsSubTotal
                            ? basket?.c_internationalProductsSubTotal
                            : getConvertedPrice(
                                  basket?.productSubTotal + giftCertificateLineItemCost,
                                  conversionsRate,
                                  false
                              ),
                        getConvertedCurrencyCode(conversionsRate, basket?.currency),
                        conversionsRate,
                        shipToCountry?.id,
                        intl
                    )}
                </Text>
            </Flex>
            <Stack spacing="16">
                <Button as={Link} to={buildUrl('/cart')} width="100%" variant="secondary">
                    {intl.formatMessage({
                        defaultMessage: 'View Cart',
                        id: 'add_to_cart_modal.link.view_cart'
                    })}
                </Button>

                <Button
                    as={Link}
                    to={buildUrl('/checkout')}
                    width="100%"
                    variant="primary"
                    onClick={handleCheckoutRedirectAction}
                >
                    {intl.formatMessage({
                        defaultMessage: 'Proceed to Checkout',
                        id: 'add_to_cart_modal.link.checkout'
                    })}
                </Button>
            </Stack>
        </>
    )
}

export const useAddToCartModal = () => {
    const {sendBasketToSplio} = useSplioService()
    const [hasCalledCustomHook, setHasCalledCustomHook] = useState(false)
    const {data: basket} = useCurrentBasket()
    const [state, setState] = useState({
        isOpen: false,
        data: null
    })

    const {pathname} = useLocation()
    useEffect(() => {
        if (state.isOpen) {
            setState({
                ...state,
                isOpen: false
            })
        }
    }, [pathname])

    if (state.isOpen && basket && basket?.productItems?.length > 0 && !hasCalledCustomHook) {
        fetchSplioCall()
        setHasCalledCustomHook(true)
    }

    async function fetchSplioCall() {
        await sendBasketToSplio({
            isCompleted: false,
            basket: basket,
            requestType: 'use-add-to-cart-modal'
        })
    }

    return {
        isOpen: state.isOpen,
        data: state.data,
        onOpen: (data) => {
            setState({
                isOpen: true,
                data
            })
        },
        onClose: () => {
            setState({
                isOpen: false,
                data: null
            })

            setHasCalledCustomHook(false)
        }
    }
}
